import React, { useCallback, useContext, useMemo, useState } from 'react';
import { appChangeWarningBody } from '@components/CrossAppChangeDialog';
import {
  AvDialog,
  CrossAppHeader,
  DescriptorType,
  EntityTypeID,
  ErrorTypes,
  FeatureFlags,
  TextInput,
  WorkflowType,
} from '@fabric-ui/components';
import { Delete, Transition } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Alert, Box, Button, Divider } from '@mui/material';
import { keepPreviousData, useMutation } from '@tanstack/react-query';
import { getParamFromURL } from '@views/CustomDashboards/components/Clickthrough/utils';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import { NotificationContext } from '../../context/AvSnackBarProvider';
import { useCustomSearchParams } from '../../hooks/UseCustomSearchParams2';
import useQuerySql from '../../hooks/useQuerySql';
import { cleanEmptyFilters, filterToExpression, normalizeBooleanParam } from '../../utils/filterUtils';
import { deserializeSortItem } from '../../utils/utils';
import { useGetFieldType } from '../CustomDashboards/hooks/useGetFieldType';
import { getHeadCellsWithSort, queryOrderBy, useBuildWhereClause } from '../Tickets/hooks';
import { defaultColumnsForEntity } from './Entities.constants';

export { isDefaultActiveView, getDefaultView, useSavedViews } from './useSavedViews';

const defaultNumberColumns = 7;

const useDataForEntity = ({ rowLimit, setSelectedViewChanged }) => {
  const [searchValue, setSearchValue] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const {
    featureFlags,
    accountEntities: { fieldTypeMap, aggProjs },
  } = useAvContext();
  const entitiesOptions = useMemo(() => Object.values(aggProjs), []);
  const [filters, setFilters] = useCustomSearchParams({
    contextSearchKey: 'entities',
  });
  const activeEntityId = getParamFromURL<string>(filters.activeEntityId);
  const activeProj = Object.values(aggProjs).find(({ entityTypeId: { name } }) => name === activeEntityId)!.projId;
  const activeProjName = Object.values(aggProjs).find(({ entityTypeId: { name } }) => name === activeEntityId)!.projId.name;
  const page = getParamFromURL<number>(filters.tablePage);
  const orderBy = filters.orderBy
    .map(deserializeSortItem)
    .filter(({ property }) => !filters.headCells || filters.headCells.includes(property));
  const filterOptions = aggProjs[activeProjName].fieldList.INTERACTIVE;
  const investigationMode = normalizeBooleanParam(getParamFromURL(filters.investigationMode));
  const { headCells: h, ...queryFilters } = filters;
  const getHeadCellType = useGetFieldType(activeProjName);

  const investigationHeadCell = (entityFieldKey, displayName?) => ({
    id: `${activeProjName}.sources.${entityFieldKey}`,
    label: (
      <>
        <Transition
          style={{
            position: 'absolute',
            right: getHeadCellType({ entityFieldKey }) === 'number' ? -42 : -34,
            color: 'var(--color-content-interactive-primary-default)',
          }}
        />
        {`Original ${displayName}`}
      </>
    ),
    ...rawColumnStyle,
  });

  const initHeadCells = projName => [
    ...aggProjs[projName].fields.reduce<any[]>(
      (acc, { name: entityFieldKey, displayName, id }, currentIndex) => [
        ...acc,
        ...(investigationMode
          ? [
              {
                hidden: defaultColumnsForEntity[activeProjName]
                  ? !defaultColumnsForEntity[activeProjName].includes(entityFieldKey)
                  : currentIndex >= defaultNumberColumns,
                ...investigationHeadCell(entityFieldKey, displayName),
              },
            ]
          : []),
        {
          hidden: defaultColumnsForEntity[activeProjName]
            ? !defaultColumnsForEntity[activeProjName].includes(entityFieldKey)
            : currentIndex >= defaultNumberColumns,
          id: id || `${activeProjName}.${entityFieldKey}`,
          label: displayName,
          isKey: entityFieldKey === '_key',
        },
      ],
      []
    ),
  ];

  const headCells = useMemo(() => {
    const initialHeadCells = initHeadCells(activeProjName);
    return getHeadCellsWithSort(
      filters.headCells
        ? [
            ...filters.headCells.flatMap(id => {
              const initCell = initialHeadCells.find(h => h.id === id);
              if (!initCell) {
                const error = `${ErrorTypes.Setup}: unknown column ${id}`;
                console.error(error);
                if (featureFlags[FeatureFlags.ShowServerErrorInToast]) {
                  enqueueSnackbar(error, { variant: 'error' });
                }
              }
              const cell = initCell || { id, label: 'Unknown' };
              return investigationMode
                ? [
                    { ...cell, hidden: false, ...investigationHeadCell(cell.id.substring(cell.id.indexOf('.') + 1), cell.label) },
                    { ...cell, hidden: false },
                  ]
                : [{ ...cell, hidden: false }];
            }),
            ...initialHeadCells
              .filter(h => !filters.headCells.includes(h.id) && !h.id.includes('.sources.'))
              .map(h => ({ ...h, hidden: true })),
          ]
        : initialHeadCells,
      orderBy
    );
  }, [filters.headCells, filters.orderBy, activeProjName, investigationMode]);
  const updateFilters = useCallback(
    (key, val, isPageChange = false) => {
      const newFilters = { ...filters, [key]: val, ...(isPageChange ? {} : { tablePage: 0 }) };
      setFilters(newFilters);
      setSelectedViewChanged(true);
    },
    [filters, setFilters]
  );

  const isFieldText = aggProjs[activeProjName].fields.reduce(
    (obj, { name }) => ({
      ...obj,
      [`${aggProjs[activeProjName].pathAlias}.${name}`]:
        fieldTypeMap[`${aggProjs[activeProjName].pathAlias}.${name}`] === DescriptorType.TYPE_STRING,
    }),
    {}
  );
  const textColumnsShown = initHeadCells(activeProjName)
    .filter(headCell => isFieldText[headCell.id] && !headCell.hidden)
    .map(headCell => headCell.id);
  const where = useBuildWhereClause({ filters: queryFilters });
  const sqlFields = headCells
    .filter(headCell => !headCell.hidden && !headCell.id.includes('tickets') && !headCell.id.includes('incidents'))
    .map(headCell => headCell.id);
  const allSQL = `SELECT ${sqlFields}
                  FROM ${activeProjName} ${where} ${queryOrderBy(orderBy, headCells)}`;
  const sql = `${allSQL} OFFSET ${page * rowLimit} LIMIT ${rowLimit}`;
  const {
    data: rows,
    isLoading,
    isRefetching,
    totals: count,
  } = useQuerySql({
    key: 'entityExplorer',
    sql,
    showTotals: true,
    options: { placeholderData: keepPreviousData },
  });
  return {
    filters,
    setFilters,
    entitiesOptions,
    searchValue,
    setSearchValue,
    updateFilters,
    headCells,
    page,
    textColumnsShown,
    activeProjName,
    activeProj,
    rows,
    isLoading,
    isRefetching,
    count,
    allSQL,
    initHeadCells,
    filterOptions,
    orderBy,
    investigationMode,
    aggProjs,
  };
};
export default useDataForEntity;

export const rawColumnStyle = {
  style: {
    transition: theme => theme.transitions.create(['background-color'], { duration: theme.transitions.duration.shorter }),
    backgroundColor: 'var(--color-surface-base-secondary)',
    '.MuiTableRow-hover:hover &': { backgroundColor: 'var(--color-surface-interactive-modifier-default)' },
  },
  sx: { backgroundColor: 'var(--color-surface-base-secondary)', zIndex: 4 },
};

interface DeleteDialogProps {
  open: boolean;
  onClose: () => any;
  onSuccess?: () => void;
  totalCount: number;
  projId: EntityTypeID;
  filter: any;
}

export const useCleanQueryFilter = filter => {
  const {
    accountEntities: { fieldTypeMap },
  } = useAvContext();
  return useMemo(() => {
    const { headCells, viewId, investigationMode, orderBy, activeEntityId, tablePage, searchArray, ...queryFilter } = filter;

    const mainExpression = cleanEmptyFilters(filterToExpression(fieldTypeMap, queryFilter));

    if (!searchArray?.[0] || searchArray.length < 2) {
      return mainExpression;
    }

    const searchExpression = {
      or: {
        operands: searchArray.slice(1).map(field => ({ expression: { fieldName: field, stringCondition: { contains: searchArray[0] } } })),
      },
    };

    if (!mainExpression) {
      return { and: { operands: [searchExpression] } };
    }

    return {
      ...mainExpression,
      and: {
        ...mainExpression.and,
        operands: [...(mainExpression.and?.operands || []), searchExpression],
      },
    };
  }, [filter]);
};
export const DeleteEntitiesDialog: React.FC<DeleteDialogProps> = ({ open, onClose, totalCount, projId, filter, onSuccess }) => {
  const {
    api,
    accountData,
    accountEntities: { aggProjs },
    hasMultipleAppsInPayingAccount,
    featureFlags,
  } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const { setRunInfo } = useContext(NotificationContext);
  const [inputValue, setInputValue] = useState('');

  const doClose = () => {
    setInputValue('');
    onClose();
  };

  const cleanFilter = useCleanQueryFilter(filter);

  const { mutate: apply } = useMutation({
    mutationFn: () => {
      if (!totalCount) {
        enqueueSnackbar('No data to delete', { variant: 'error' });
        return Promise.reject(new Error(`${ErrorTypes.Validation}: no data to delete`));
      }
      if (!cleanFilter) {
        enqueueSnackbar('Filter is required', { variant: 'error' });
        return Promise.reject(new Error(`${ErrorTypes.Validation}: filter is required`));
      }

      doClose();

      return api(DELETE, { options: { projId, filter: cleanFilter } }).then(({ data }) =>
        setRunInfo({
          runId: data.deleteByFilter.workflowRun.runId,
          wfId: data.deleteByFilter.workflowRun.id, // TODO change id to wfId and remove .workflowRun
          returnStatusOnly: true,
          workflowType: WorkflowType.DataDeletion,
          onSuccess,
        })
      );
    },
  });

  const isDeleteDisabled = inputValue !== accountData.name;

  return (
    <AvDialog
      open={open}
      onClose={doClose}
      titleVariant="error"
      noPadding
      title="Confirm Deletion"
      footer={
        <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
          <Button onClick={doClose}>Cancel</Button>
          <Button onClick={() => apply()} variant="contained" color="error" disabled={isDeleteDisabled}>
            <Delete />
            Delete
          </Button>
        </Box>
      }
      header={hasMultipleAppsInPayingAccount && featureFlags[FeatureFlags.CrossAppChangePopUp] && <CrossAppHeader />}>
      <Box sx={{ px: 3, ...flex.col, gap: 2, maxWidth: 620 }}>
        <div>
          You are about to permanently delete{' '}
          <span style={{ fontWeight: 600 }}>
            {totalCount} {aggProjs[projId.name].projDisplayName}
          </span>{' '}
          records that match the current filter.
          {hasMultipleAppsInPayingAccount && featureFlags[FeatureFlags.CrossAppChangePopUp] && appChangeWarningBody()}
          <div>Please note that as part of the deletion process, relevant dependent records will be recalculated.</div>
        </div>
        <Alert severity="error" icon={false}>
          All data will be permanently deleted from the system and cannot be restored
        </Alert>
        <Box sx={{ ...flex.col, pt: 1 }}>
          <Divider flexItem />
        </Box>
        <Box sx={{ userSelect: 'none' }}>{`To confirm, type “${accountData.name}” in the box below`}</Box>
        <Box sx={{ pb: 1 }}>
          <TextInput
            sx={{ width: '100%', maxWidth: 'unset' }}
            isRequired
            onChange={setInputValue}
            value={inputValue}
            placeholder="Type the text above"
          />
        </Box>
      </Box>
    </AvDialog>
  );
};

const DELETE = gql`
  mutation deleteByFilter($projId: ProjectionIDInput!, $filter: filterScalar!) {
    deleteByFilter(projId: $projId, filter: $filter) {
      workflowRun {
        id
        runId
      }
    }
  }
`;
